import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { toast } from "sonner";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { History, RefreshCw, FileSpreadsheet, Sparkles, Database, Repeat, Loader2, AlertTriangle, PlayCircle, Undo2, LifeBuoy, Download, ShieldCheck, RefreshCcwDot, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { AiImportDialog } from "./AiImportDialog";
import { ImportRecoverDialog } from "./ImportRecoverDialog";
import { useAuth } from "../contexts/AuthContext";

const fmtWhen = (s) => (s ? new Date(s).toLocaleString() : "—");

export function ImportRunsList({ reloadKey }) {
  const [runs, setRuns] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [preset, setPreset] = useState(null);
  const [resumeId, setResumeId] = useState(null);
  const [recoverRun, setRecoverRun] = useState(null);
  const [rollbackBusy, setRollbackBusy] = useState(null);
  const [restoreBusy, setRestoreBusy] = useState(null);
  const [confirmUndo, setConfirmUndo] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(null);
  const { user } = useAuth();
  const canSweep = ["tenant_admin", "esg_manager", "super_admin"].includes(user?.role);
  const [fx, setFx] = useState(null);
  const [sweepBusy, setSweepBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/esg-import/runs").then((r) => setRuns(r.data.runs)).catch(() => setRuns([]));
    api.get("/esg-import/fx-status").then((r) => setFx(r.data)).catch(() => setFx(null));
  }, []);
  const runSweep = async () => {
    setSweepBusy(true);
    try {
      const { data } = await api.post("/esg-import/fx-sweep");
      toast.success(`FX sweep: ${data.records_updated} record(s) resolved, ${data.pending_remaining} still pending`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSweepBusy(false); }
  };
  useEffect(() => { load(); }, [load, reloadKey]);

  // live-refresh while a background workbook analysis is running
  useEffect(() => {
    if (!(runs || []).some((r) => r.status === "analyzing")) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [runs, load]);

  // Only the MOST RECENT committed run per tenant can be rolled back (matches backend).
  const rollbackable = useMemo(() => {
    const byTenant = {};
    (runs || []).forEach((r) => {
      if (r.status === "committed" && r.committed_at) {
        const t = r.tenant_id || "_";
        if (!byTenant[t] || new Date(r.committed_at) > new Date(byTenant[t].committed_at)) byTenant[t] = r;
      }
    });
    return new Set(Object.values(byTenant).map((r) => r.import_id));
  }, [runs]);

  const doUndo = async (run) => {
    setRollbackBusy(run.import_id);
    try {
      const { data } = await api.post(`/esg-import/${run.import_id}/rollback`);
      setConfirmUndo(null);
      const restoredNote = data.restored_superseded > 0
        ? ` ${data.restored_superseded} superseded original${data.restored_superseded === 1 ? " was" : "s were"} restored.`
        : "";
      toast.success(`Import undone — ${data.removed} record${data.removed === 1 ? "" : "s"} moved to recovery.${restoredNote} Restore is available until your next import is committed.`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setRollbackBusy(null); }
  };

  const restore = async (run) => {
    setRestoreBusy(run.import_id);
    try {
      const { data } = await api.post(`/esg-import/${run.import_id}/restore`);
      toast.success(`Restored ${data.restored} record${data.restored === 1 ? "" : "s"} from "${run.filename}"`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setRestoreBusy(null); }
  };

  const verifyRun = async (run) => {
    setVerifyBusy(run.import_id);
    try {
      const { data } = await api.post(`/esg-import/${run.import_id}/verify`);
      toast.success(`Verified ${data.verified} record${data.verified === 1 ? "" : "s"} from this import`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setVerifyBusy(null); }
  };

  const downloadSkipped = async (run) => {
    try {
      const res = await api.get(`/esg-import/${run.import_id}/skipped.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `skipped_${run.filename || "import"}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const rerun = (run) => {
    setResumeId(null);
    setPreset({ detected_type: run.detected_type, headers: run.headers || [], learned_count: run.learned_count || 0, tenant_id: run.tenant_id });
    setImportOpen(true);
  };
  const resume = (run) => {
    setPreset(null);
    setResumeId(run.import_id);
    setImportOpen(true);
  };
  const lastMemoryRun = (runs || []).find((r) => r.memory_ready);
  const showTenant = (runs || []).some((r) => r.tenant_name);

  if (runs === null) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading import runs…</div>;

  return (
    <div className="space-y-3" data-testid="import-runs-list">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-eyebrow inline-flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> AI Bulk Import Runs ({runs.length})</div>
        <div className="flex items-center gap-2">
          {lastMemoryRun && (
            <Button variant="primary" size="sm" onClick={() => rerun(lastMemoryRun)} data-testid="rerun-last-format-btn" title={`Re-run "${lastMemoryRun.detected_type || lastMemoryRun.filename}" with the learned mapping`}>
              <Repeat className="h-3.5 w-3.5" /> Re-run last format
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="import-runs-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
        </div>
      </div>

      {fx && (
        <div className={`card-flat p-2.5 flex items-center justify-between gap-3 ${fx.pending_records > 0 ? "border-[#FFB800]/40" : ""}`} data-testid="fx-sweep-panel">
          <div className="flex items-center gap-2 text-xs">
            <RefreshCcwDot className={`h-4 w-4 ${fx.pending_records > 0 ? "text-[#FFB800]" : "text-muted-foreground"}`} />
            {fx.pending_records > 0 ? (
              <span data-testid="fx-pending-count"><span className="font-semibold text-[#FFB800]">{fx.pending_records}</span> foreign-currency record(s) awaiting an FX rate{fx.escalated_records > 0 ? <> · <span className="font-semibold text-red-400" data-testid="fx-escalated-count">{fx.escalated_records} escalated ({fx.escalate_days}d+)</span></> : ""}.</span>
            ) : (
              <span className="text-muted-foreground" data-testid="fx-pending-count">All foreign-currency records converted. FX retry sweep runs daily.</span>
            )}
            {fx.last_sweep_at && <span className="text-[10px] text-muted-foreground">· last swept {fmtWhen(fx.last_sweep_at)}</span>}
          </div>
          {canSweep && (
            <Button variant="outline" size="sm" onClick={runSweep} disabled={sweepBusy} data-testid="fx-sweep-now-btn" title="Retry pending FX conversions now">
              {sweepBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcwDot className="h-3.5 w-3.5" />} Run sweep now
            </Button>
          )}
        </div>
      )}

      {runs.length === 0 ? (
        <div className="card-flat p-10 text-center">
          <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No bulk imports yet. Use the <span className="font-medium text-foreground">AI Import</span> button on the ESG Records tab to drop a CSV/Excel file.</p>
        </div>
      ) : (
        <div className="card-flat overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                {showTenant && <TableHead>Tenant</TableHead>}
                <TableHead>Detected type</TableHead>
                <TableHead>Rows</TableHead>
                <TableHead>ESG / Not</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>By</TableHead>
                <TableHead>When</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id} data-testid={`import-run-${r.id}`}>
                  <TableCell className="text-sm font-medium max-w-[200px]">
                    <span className="inline-flex items-center gap-1.5 truncate" title={r.filename}>
                      <FileSpreadsheet className="h-3.5 w-3.5 text-accent shrink-0" />
                      <span className="truncate">{r.filename}</span>
                    </span>
                    <div className="mt-0.5 flex gap-1">
                      {r.used_ai && <Badge variant="success" className="text-[8px]">AI</Badge>}
                      {r.used_memory && <Badge variant="default" className="text-[8px]">⚡ Memory</Badge>}
                    </div>
                  </TableCell>
                  {showTenant && <TableCell className="text-xs font-medium" data-testid={`import-run-tenant-${r.id}`}>{r.tenant_name || "—"}</TableCell>}
                  <TableCell className="text-xs text-muted-foreground">{r.detected_type || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{r.analyzed_rows}{r.total_rows > r.analyzed_rows ? `/${r.total_rows}` : ""}</TableCell>
                  <TableCell className="text-xs"><span className="text-[#00E65B] font-mono">{r.accepted}</span> / <span className="text-muted-foreground font-mono">{r.rejected}</span></TableCell>
                  <TableCell className="text-xs">
                    <div className="flex flex-wrap gap-1">
                      {r.duplicate_count > 0 && <Badge variant="warning" className="text-[8px]">{r.duplicate_count} dup</Badge>}
                      {r.derived_total_count > 0 && <Badge variant="danger" className="text-[8px]">{r.derived_total_count} derived</Badge>}
                      {r.rollup_annual_count > 0 && <Badge variant="default" className="text-[8px]" title="Annual roll-up rows held out of commit to avoid double-counting" data-testid={`import-run-rollup-${r.id}`}>{r.rollup_annual_count} roll-up</Badge>}
                      {r.low_confidence_count > 0 && <Badge variant="default" className="text-[8px]">{r.low_confidence_count} review</Badge>}
                      {r.skipped_count > 0 && <Badge variant="warning" className="text-[8px]" title="Rows analysed but not committed — open the run for the skipped-rows report" data-testid={`import-run-skipped-${r.id}`}>{r.skipped_count} skipped</Badge>}
                      {!r.duplicate_count && !r.low_confidence_count && !r.derived_total_count && !r.rollup_annual_count && <span className="text-muted-foreground">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {r.status === "committed" ? (
                      <div className="space-y-0.5">
                        <Badge variant="success" className="text-[9px] inline-flex items-center gap-1" data-testid={`import-run-status-${r.id}`}><Database className="h-2.5 w-2.5" /> Committed · {r.committed}</Badge>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(r.by_pillar || {}).filter(([, n]) => n > 0).map(([p, n]) => <span key={p} className="text-[9px] text-muted-foreground">{p[0]}:{n}</span>)}
                        </div>
                      </div>
                    ) : r.status === "analyzing" ? (
                      <Badge variant="default" className="text-[9px] inline-flex items-center gap-1" data-testid={`import-run-status-${r.id}`}><Loader2 className="h-2.5 w-2.5 animate-spin" /> Analyzing…</Badge>
                    ) : r.status === "failed" ? (
                      <Badge variant="danger" className="text-[9px] inline-flex items-center gap-1" data-testid={`import-run-status-${r.id}`}><AlertTriangle className="h-2.5 w-2.5" /> Failed</Badge>
                    ) : r.status === "rolled_back" ? (
                      <Badge variant="outline" className="text-[9px] inline-flex items-center gap-1 border-[#FFB800]/40 text-[#FFB800]" data-testid={`import-run-status-${r.id}`}><Undo2 className="h-2.5 w-2.5" /> Undone · {r.rolled_back_count || 0}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[9px]" data-testid={`import-run-status-${r.id}`}>Staged (not committed)</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-[11px] text-muted-foreground max-w-[140px] truncate" title={r.created_by}>{r.created_by || "—"}</TableCell>
                  <TableCell className="text-[10px] font-mono text-muted-foreground">{fmtWhen(r.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1.5 justify-end flex-wrap">
                      {r.multi_sheet && (r.status === "staged" || r.status === "analyzing") && (
                        <Button variant="primary" size="sm" className="h-7" onClick={() => resume(r)} data-testid={`resume-run-${r.id}`}
                          title={r.status === "analyzing" ? "Analysis is still running — open the live progress view" : "Analysis is ready — open the review screen to commit"}>
                          {r.status === "analyzing" ? <><Loader2 className="h-3 w-3 animate-spin" /> Progress</> : <><PlayCircle className="h-3 w-3" /> Resume review</>}
                        </Button>
                      )}
                      {(r.skipped_count > 0 || r.status === "staged") && (
                        <Button variant="outline" size="sm" className="h-7" onClick={() => downloadSkipped(r)} data-testid={`download-skipped-${r.id}`}
                          title="Download every held/skipped row of this run, with its reason, as a CSV to fix offline">
                          <Download className="h-3 w-3" /> Skipped CSV
                        </Button>
                      )}
                      {r.status === "committed" && r.skipped_count > 0 && (
                        <Button variant="outline" size="sm" className="h-7" onClick={() => setRecoverRun(r)} data-testid={`recover-run-${r.id}`}
                          title={`Force-include the ${r.skipped_count} held/skipped row(s) without re-uploading`}>
                          <LifeBuoy className="h-3 w-3" /> Recover {r.skipped_count}
                        </Button>
                      )}
                      {r.status === "committed" && r.unverified_count > 0 && (
                        <Button variant="outline" size="sm" className="h-7 text-[#00E65B] border-[#00E65B]/30" onClick={() => verifyRun(r)}
                          disabled={verifyBusy === r.import_id} data-testid={`verify-run-${r.id}`}
                          title={`Promote all ${r.unverified_count} Unverified record(s) from this import to Verified`}>
                          {verifyBusy === r.import_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />} Verify {r.unverified_count}
                        </Button>
                      )}
                      {r.status === "committed" && rollbackable.has(r.import_id) && (
                        <Button variant="outline" size="sm" className="h-7 text-red-400 border-red-500/30" onClick={() => setConfirmUndo(r)}
                          disabled={rollbackBusy === r.import_id} data-testid={`undo-run-${r.id}`}
                          title="Undo this import — moves the records it created into recovery (most recent import only). You can Restore them afterwards.">
                          {rollbackBusy === r.import_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Undo
                        </Button>
                      )}
                      {r.status === "rolled_back" && r.restorable && (
                        <Button variant="outline" size="sm" className="h-7 text-[#00E65B] border-[#00E65B]/30" onClick={() => restore(r)}
                          disabled={restoreBusy === r.import_id} data-testid={`restore-run-${r.id}`}
                          title="Restore this undone import — puts the records back (available until your next import is committed)">
                          {restoreBusy === r.import_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Restore
                        </Button>
                      )}
                      {r.memory_ready && (r.status === "committed" || r.status === "rolled_back") && (
                        <Button variant="outline" size="sm" className="h-7" onClick={() => rerun(r)} data-testid={`rerun-run-${r.id}`} title={`Re-upload a new file in this format — ${r.learned_count} learned line items`}>
                          <Repeat className="h-3 w-3" /> Re-run
                        </Button>
                      )}
                      {!(r.multi_sheet && (r.status === "staged" || r.status === "analyzing")) && !(r.status === "committed" && (r.skipped_count > 0 || rollbackable.has(r.import_id) || r.unverified_count > 0)) && !(r.status === "rolled_back" && r.restorable) && !(r.memory_ready && (r.status === "committed" || r.status === "rolled_back")) && !(r.skipped_count > 0 || r.status === "staged") && (
                        <span className="text-[10px] text-muted-foreground" title="No learned mapping yet — commit at least one import of this format to enable instant re-runs">—</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">Every AI bulk import is logged here for auditability — including runs that were analyzed but never committed. Use <span className="font-medium text-foreground">Re-run</span> to drop the next period's file in a known format: matching rows auto-classify from memory (fewer AI tokens) for a repeatable monthly close.</p>

      <AiImportDialog open={importOpen} onClose={() => { setImportOpen(false); setResumeId(null); load(); }} onImported={load} presetFormat={preset} resumeImportId={resumeId} />
      <ImportRecoverDialog open={!!recoverRun} run={recoverRun} onClose={() => setRecoverRun(null)} onDone={load} />

      <Dialog open={!!confirmUndo} onOpenChange={(o) => { if (!o && !rollbackBusy) setConfirmUndo(null); }}>
        <DialogContent className="w-[94vw] max-w-md" data-testid="undo-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-400"><Undo2 className="h-5 w-5" /> Undo this import?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This removes the <span className="font-semibold text-foreground" data-testid="undo-confirm-count">{confirmUndo?.committed}</span> record{confirmUndo?.committed === 1 ? "" : "s"} that <span className="font-medium text-foreground">“{confirmUndo?.filename}”</span> added to the inventory.
            </p>
            {confirmUndo?.replaced_count > 0 && (
              <div className="card-flat p-2.5 text-[11px] border-[#FFB800]/50 bg-[#FFB800]/10 text-[#FFB800] flex items-start gap-2" data-testid="undo-confirm-replace-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>This import <span className="font-semibold">replaced {confirmUndo.replaced_count} existing record{confirmUndo.replaced_count === 1 ? "" : "s"}</span>. Undoing will bring {confirmUndo.replaced_count === 1 ? "that original" : "those originals"} back and return Scope 1 totals to their pre-import values.</span>
              </div>
            )}
            <div className="card-flat p-2.5 text-[11px] text-muted-foreground border-[#00E65B]/30" data-testid="undo-confirm-restore-note">
              Not permanent — the records move to a recovery cache and you can <span className="text-[#00E65B] font-medium">Restore</span> them until your next import is committed.
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setConfirmUndo(null)} disabled={rollbackBusy === confirmUndo?.import_id} data-testid="undo-cancel-btn">Cancel</Button>
              <Button variant="destructive" onClick={() => doUndo(confirmUndo)} disabled={rollbackBusy === confirmUndo?.import_id} data-testid="undo-confirm-btn">
                {rollbackBusy === confirmUndo?.import_id ? <><Loader2 className="h-4 w-4 animate-spin" /> Undoing…</> : <><Undo2 className="h-4 w-4" /> Undo import</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
